import InscreenObserver from '@finn-no/inscreen-observer';

export default {
    dependencies: [
        '$element',
        'recommendationsservice',
        'templateservice',
        'trackingservice',
        'radio',
    ],
    callable(
        ele,
        recommendationService,
        templateService,
        trackingService,
        radio
    ) {
        const vertical = ele.getAttribute('data-vertical');
        const position = ele.getAttribute('data-position');
        const adid = ele.getAttribute('data-adid');
        const isNewDesign = ele.hasAttribute('data-new-design');
        const size = isNewDesign ? 6 : ele.getAttribute('data-size');
        let spinnerEle;

        function trackEmpty(data) {
            trackingService.trackEventObject({
                ecType: 'USER',
                ecCategory: 'recommendationEvents',
                eventData: {
                    version: data.version,
                    'recsys.position': `recommend.m-${position}.empty`,
                },
            });
        }

        function trackViewed(data) {
            data.items.forEach(ad => {
                if (ad.trackingCode) {
                    trackingService.trackEventObject({
                        ecType: 'AD',
                        ecCategory: 'LOG_PROFILED_ADS_FRONT_VIEW',
                        eventData: {
                            'recsys.trackingcode': ad.trackingCode,
                            'recsys.score': ad.score,
                            'recsys.uuid': data.uuid,
                        },
                        // Context finnkode must be overriden to avoid inheriting the finnkode from the object page it is on.
                        context: {
                            finnkode: ad.adId || ad.itemId,
                        },
                    });
                }
            });

            const adIds = data.items.map(ad => ad.itemId).join(',');
            trackingService.trackEventObject({
                ecType: 'USER',
                ecCategory: 'recommendationEvents',
                eventData: {
                    adIds,
                    version: data.version,
                    'recsys.uuid': data.uuid,
                    'recsys.position': `recommend.m-${position}.show`,
                },
            });
        }

        function trackInscreenOnce(data) {
            const inscreenObserver = new InscreenObserver(
                () => {
                    const adIds = data.items.map(ad => ad.itemId).join(',');
                    trackingService.trackEventObject({
                        ecType: 'USER',
                        ecCategory: 'recommendationEvents',
                        eventData: {
                            adIds,
                            version: data.version,
                            'recsys.uuid': data.uuid,
                            'recsys.position': `recommend.m-${position}.inscreen`,
                        },
                    });
                },
                { once: true }
            );
            inscreenObserver.observe(ele);
        }

        function attributeOrEmpty(event, name) {
            const ele = event.target.closest(`[${name}]`);
            return ele ? ele.getAttribute(name) : '';
        }

        function handleClickCross(event) {
            const versionEle = event.target.closest('[data-version]');
            event.preventDefault();
            event.stopPropagation();
            event.target.parentElement.disabled = true;

            trackingService.trackEventObject({
                ecType: 'USER',
                ecCategory: 'recommend.block',
                eventData: {
                    version: versionEle
                        ? versionEle.getAttribute('data-version')
                        : '',
                    'recsys.position': position,
                    'recsys.uuid': attributeOrEmpty(event, 'data-uuid'),
                },
                context: {
                    finnkode: attributeOrEmpty(event, 'data-finncode'),
                },
            });

            versionEle.style.transition = 'opacity .2s';
            versionEle.style.opacity = '.15';
        }

        function trackClicked(event) {
            const finncode = attributeOrEmpty(event, 'data-finncode');
            const uuid = attributeOrEmpty(event, 'data-uuid');
            trackingService.trackLaterObj({
                ecType: 'USER',
                ecCategory: 'recommendationEvents',
                eventData: {
                    version: attributeOrEmpty(event, 'data-version'),
                    'recsys.uuid': uuid,
                    'recsys.position': `recommend.m-${position}.click`,
                },
                context: {
                    finnkode: finncode,
                },
            });

            const trackingCode = attributeOrEmpty(event, 'data-trackingcode');

            if (trackingCode) {
                trackingService.trackLaterObj({
                    ecType: 'AD',
                    ecCategory: 'LOG_PROFILED_ADS_FRONT_CLICK',
                    eventData: {
                        'recsys.trackingcode': trackingCode,
                        'recsys.score': attributeOrEmpty(event, 'data-score'),
                        'recsys.uuid': uuid,
                    },
                    context: {
                        finnkode: finncode,
                    },
                });
            }
        }

        function addClickEvents() {
            Array.from(ele.getElementsByTagName('a'), link =>
                link.addEventListener('click', trackClicked)
            );
            Array.from(
                ele.querySelectorAll('[data-recommendation-block]'),
                cross => cross.addEventListener('click', handleClickCross)
            );
        }

        function onRecommendationSucceeded(response) {
            if (!response) {
                ele.remove();
                return;
            }
            if (isEmptyRecommendation(response)) {
                ele.remove();
                trackEmpty(response);
                return;
            }
            renderItemsResult(response);
            addClickEvents();
            trackViewed(response);
            trackInscreenOnce(response);
        }

        function onRecommendationFailed() {
            ele.remove();
        }

        function isEmptyRecommendation(response) {
            return response.items && response.items.length === 0;
        }

        function renderItemsResult(result) {
            const moreLikeThis = adid !== null;
            const renderItems = result.items.map(item =>
                Object.assign({}, item, {
                    image: Object.assign({}, item.image, {
                        isLogo: item.image.type === 'LOGO',
                    }),
                })
            );
            const renderResult = Object.assign({}, result, {
                recommendations: {
                    heading: moreLikeThis
                        ? 'Lignende annonser'
                        : 'Anbefalte annonser',
                    uuid: result.uuid,
                    mlt: moreLikeThis,
                },
                items: renderItems,
            });

            ele.style.opacity = '0';
            ele.classList.remove('hidden', 'u-display-none');
            ele.style.transition = 'opacity 0.6s';
            ele.innerHTML = templateService.render(
                isNewDesign ? 'recommendations_stream' : 'recommendation_items',
                renderResult
            );
            window.requestAnimationFrame(() => {
                ele.style.opacity = '1';
            });
        }

        function onRefreshRecommendationsWithCareerPreferences() {
            renderLoadingSpinner();

            recommendationService
                .getRecommendations(vertical, adid, size)
                .then(onRecommendationSucceeded, onRecommendationFailed)
                .then(removeSpinner, removeSpinner);
        }

        function renderLoadingSpinner() {
            spinnerEle =
                ele.parentElement &&
                ele.parentElement.querySelector('[data-spinner]');
            ele.style.opacity = '0.15';
            spinnerEle.classList.remove('hidden');
        }

        function removeSpinner() {
            spinnerEle.classList.add('hidden');
            ele.style.opacity = '1';
        }

        recommendationService
            .getRecommendations(vertical, adid, size)
            .then(onRecommendationSucceeded, onRecommendationFailed);

        radio('recommendations:withCP:fetch').subscribe(
            onRefreshRecommendationsWithCareerPreferences
        );
    },
};



// WEBPACK FOOTER //
// ./controllers/recommendations.js